/**
 *  @swagger
 *  definitions:
 *    ChampionshipDocumentParams:
 *      type: object
 *      properties:
 *        ChampionshipId:
 *          description: ID of the associated Championship
 *          type: string
 *        Public:
 *          description: Public of the ChampionshipDocument
 *          type: boolean
 *        Name:
 *          description: Name of the ChampionshipDocument
 *          type: string
 *        DocumentDate:
 *          description: Document Date of the RegattaDocument
 *          type: string
 *          format: date-time
 *        PublishDate:
 *          description: Publish Date of the RegattaDocument
 *          type: string
 *          format: date-time
 *        Status:
 *          description: Status of the ChampionshipDocument
 *          $ref: '#/components/enums/ChampionshipDocumentStatus'
 *        Content:
 *          description: Content of the ChampionshipDocument
 *          type: string
 *        File:
 *          description: File of the ChampionshipDocument
 *          type: string
 *        StatusContent:
 *          description: Status Content of the ChampionshipDocument
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    ChampionshipDocumentCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipDocumentParams'
 *        - type: object
 *          required:
 *            - ChampionshipId
 *            - Name
 *            - DocumentDate
 *            - Status
 *
 *  @swagger
 *  definitions:
 *    ChampionshipDocumentQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipDocument
 *              type: string
 *        - $ref: '#/definitions/ChampionshipDocumentParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipDocumentUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipDocumentQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipDocument:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipDocumentCreateParams'
 *        - $ref: '#/definitions/ChampionshipDocumentUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipDocument:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipDocument'
 *          - type: object
 *            properties:
 *              Championship:
 *                description: Championship of the ChampionshipDocument
 *                $ref: '#/components/schemas/Championship'
 */

// Enums
import {
  ChampionshipDocumentStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipId: string;
};

export type $Params = {
  Content: null | string;
  DocumentDate: Date;
  File: null | string;
  Name: string;
  Public: boolean;
  PublishDate?: Date | null;
  Status: ChampionshipDocumentStatus;
  StatusContent: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Championship?: $Model<'Championship'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  DocumentDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  File: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  PublishDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(ChampionshipDocumentStatus)),
    validate: {
      notEmpty: true,
    },
  },
  StatusContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipDocument'> => {
  const ChampionshipDocument = stack.define(
    'ChampionshipDocument',
    fields(Sequelize),
    {
      tableName: 'ChampionshipDocuments',
    },
  );

  ChampionshipDocument.associate = (models: $ModelClasses) => {
    models.ChampionshipDocument.belongsTo(
      models.Championship,
      {
        as: 'Championship',
        foreignKey: 'ChampionshipId',
      },
    );
  };

  ChampionshipDocument.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipDocument;
});
